import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { CityParkingCards } from '../../../../core/models/parking-card.model';
import { MapperParkingCardListService } from '../../../../core/mappers/parking/mapper-parking-card-list.service';
import { ApiParkingCardListService } from '../../../../core/services/api/parking/api-parking-card-list.service';
import { SiteStateService } from '../../../../core/services/site-state.service';

type DeletedSearchType = 'card_name' | 'card_code' | 'name' | 'uid' | 'license';

/**
 * รายการบัตรที่ยกเลิกแล้ว — parking_card.deleted = true
 * UI โครงเดียวกับ parking-cards แต่ดูอย่างเดียว (ไม่มี Dashboard / ปุ่มเพิ่ม)
 */
@Component({
  selector: 'app-parking-cards-deleted',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent],
  templateUrl: './parking-cards-deleted.component.html',
  styleUrl: './parking-cards-deleted.component.css',
})
export class ParkingCardsDeletedComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiParkingCardListService);
  private readonly mapper = inject(MapperParkingCardListService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);

  rows: CityParkingCards[] = [];

  searchType: DeletedSearchType = 'card_name';
  searchText = '';
  isSearchTypeDropdownOpen = false;

  sortColumn = 'writedate';
  sortDirection: 'asc' | 'desc' = 'desc';

  currentPage = 1;
  pageSize = 20;
  pageSizeOptions = [20, 50, 100];
  totalItems = 0;
  totalPages = 1;

  isLoading = false;
  lastFetchedTime: Date | null = null;
  userId = 0;

  isRemarkModalOpen = false;
  remarkModalCard: CityParkingCards | null = null;

  constructor() {
    const userIdLocal = localStorage.getItem('userId');
    this.userId = userIdLocal
      ? Number(userIdLocal)
      : Number(this.cookieService.get('user_id'));

    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id || !this.userId) return;
      untracked(() => {
        this.currentPage = 1;
        this.loadTable();
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get pagesArray(): number[] {
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(this.totalPages, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  get currentSearchTypeLabel(): string {
    switch (this.searchType) {
      case 'card_name':
        return 'หมายเลขบัตร';
      case 'card_code':
        return 'รหัสระบบ';
      case 'name':
        return 'ชื่อลูกค้า';
      case 'uid':
        return 'UID';
      case 'license':
        return 'ทะเบียนรถ';
      default:
        return 'ค้นหา';
    }
  }

  ngOnInit(): void {
    if (!this.userId) {
      const userIdLocal = localStorage.getItem('userId');
      this.userId = userIdLocal
        ? Number(userIdLocal)
        : Number(this.cookieService.get('user_id'));
    }
    if (this.selectedSiteId && this.userId) {
      this.loadTable();
    }
  }

  onRefresh(): void {
    this.currentPage = 1;
    this.loadTable();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadTable();
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTable();
    }
  }

  onPageSizeChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);
    if (!Number.isNaN(value)) {
      this.pageSize = value;
    }
    this.currentPage = 1;
    this.loadTable();
  }

  toggleSearchTypeDropdown(): void {
    this.isSearchTypeDropdownOpen = !this.isSearchTypeDropdownOpen;
  }

  selectSearchType(type: DeletedSearchType): void {
    this.searchType = type;
    this.isSearchTypeDropdownOpen = false;
  }

  sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.loadTable();
  }

  getPlateDisplay(card: CityParkingCards): string {
    const plate = card.licenseList?.[0]?.plateNumber?.trim();
    if (!plate) return '-';
    const province = card.licenseList?.[0]?.province?.trim();
    return province ? `${plate} ${province}` : plate;
  }

  hasPlate(card: CityParkingCards): boolean {
    return Boolean(card.licenseList?.[0]?.plateNumber?.trim());
  }

  getVehicleIcon(vehicleType: string | null | undefined): string {
    if (!vehicleType) return 'directions_car';
    const type = vehicleType.toLowerCase();
    if (
      type.includes('motor') ||
      type.includes('bike') ||
      type.includes('จักรยาน') ||
      type.includes('มอเตอร์')
    ) {
      return 'two_wheeler';
    }
    return 'directions_car';
  }

  getRowNumber(index: number): number {
    return (this.currentPage - 1) * this.pageSize + index + 1;
  }

  trackByCardId(_: number, item: CityParkingCards): number {
    return item.id;
  }

  openRemarkDetail(card: CityParkingCards): void {
    this.remarkModalCard = card;
    this.isRemarkModalOpen = true;
  }

  closeRemarkDetail(): void {
    this.isRemarkModalOpen = false;
    this.remarkModalCard = null;
  }

  get remarkModalTitle(): string {
    const c = this.remarkModalCard;
    if (!c) return 'รายละเอียดการยกเลิก';
    const parts = [c.cardName, c.cardCode].filter(Boolean);
    return parts.length ? parts.join(' · ') : `บัตร #${c.id}`;
  }

  private mapSearchField(): string {
    switch (this.searchType) {
      case 'card_name':
        return 'cardname';
      case 'card_code':
        return 'cardcode';
      case 'name':
        return 'name';
      case 'uid':
        return 'uid';
      case 'license':
        return 'license';
      default:
        return 'cardcode';
    }
  }

  loadTable(): void {
    if (!this.selectedSiteId || !this.userId) {
      this.rows = [];
      this.totalItems = 0;
      this.totalPages = 1;
      return;
    }

    this.isLoading = true;
    this.api
      .getDeletedParkingCardForTable({
        siteId: this.selectedSiteId,
        userId: this.userId,
        page: this.currentPage,
        pageSize: this.pageSize,
        searchField: this.mapSearchField(),
        searchText: this.searchText?.trim() || undefined,
        sortColumn: this.sortColumn,
        sortDirection: this.sortDirection,
      })
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapTableFromDto(res);
          this.rows = mapped.items;
          this.totalItems = mapped.total;
          this.totalPages = Math.max(
            1,
            mapped.totalPages || Math.ceil(mapped.total / this.pageSize) || 1
          );
          this.currentPage = mapped.page || this.currentPage;
          this.lastFetchedTime = new Date();
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          this.rows = [];
          this.totalItems = 0;
          this.totalPages = 1;
          this.toast.error(
            err?.error?.message || err?.message || 'โหลดรายการบัตรที่ยกเลิกไม่สำเร็จ'
          );
        },
      });
  }
}
